'use client';

/**
 * Nexus Personal – Client-Helfer fuer den EINEN durchgehenden Faden pro User
 * (Plan: docs/NEXUS-PERSONAL-PLAN.md, Phase 1 + 3).
 *
 * Backend-Vertrag:
 *   GET  /api/ai/personal/main?before_seq=&limit=30
 *        → { conversation_id, messages:[{seq, role, parts, created_at}], has_more }
 *   POST /api/nexus-personal/card-action
 *        { conversation_id, seq, action_id, payload? } → { ok, message }
 *
 * "Zuletzt gesehen" (last_seen_seq) liegt pro Faden im localStorage. Der Faden
 * ist pro org+user eindeutig (deterministische conversation_id), daher ist der
 * Key automatisch user-/org-getrennt.
 */

import { BackendAuth } from '@/lib/auth-backend';

export const NEXUS_PERSONAL_PAGE_SIZE = 30;
/** Polling-Intervall bei OFFENEM Panel (neue Proaktiv-Meldungen live sehen). */
export const NEXUS_PERSONAL_OPEN_POLL_MS = 20_000;
const LS_PREFIX = 'nexus_personal_last_seen_seq:';

/** Eine Seite des Hauptfadens laden (neueste Seite ohne beforeSeq). */
export async function fetchMainThread({ beforeSeq = null, limit = NEXUS_PERSONAL_PAGE_SIZE } = {}) {
  const qs = new URLSearchParams();
  if (beforeSeq != null) qs.set('before_seq', String(beforeSeq));
  qs.set('limit', String(limit));
  const res = await BackendAuth.apiCallJson(`/api/ai/personal/main?${qs.toString()}`);
  if (!res) return null; // 401 → BackendAuth hat bereits abgemeldet
  const messages = Array.isArray(res.messages)
    ? res.messages.filter((m) => m && Number.isFinite(Number(m.seq)))
        .map((m) => ({ ...m, seq: Number(m.seq) }))
        .sort((a, b) => a.seq - b.seq)
    : [];
  return {
    conversation_id: res.conversation_id || null,
    messages,
    has_more: !!res.has_more,
  };
}

/** Karten-Aktion ausfuehren (send_reply / create_contact / ignore). */
export async function postCardAction({ conversationId, seq, actionId, payload }) {
  const body = { conversation_id: conversationId, seq, action_id: actionId };
  if (payload && typeof payload === 'object') body.payload = payload;
  return BackendAuth.apiCallJson('/api/nexus-personal/card-action', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function getLastSeenSeq(conversationId) {
  if (!conversationId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + conversationId);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Nur vorwaerts setzen – ein aelterer Wert ueberschreibt nie einen neueren. */
export function setLastSeenSeq(conversationId, seq) {
  if (!conversationId || !Number.isFinite(Number(seq)) || typeof window === 'undefined') return;
  try {
    const prev = getLastSeenSeq(conversationId);
    if (prev != null && prev >= Number(seq)) return;
    window.localStorage.setItem(LS_PREFIX + conversationId, String(Number(seq)));
  } catch { /* localStorage nicht verfuegbar */ }
}

export function maxSeqOf(rows) {
  let max = null;
  for (const r of rows || []) {
    if (Number.isFinite(r?.seq) && (max == null || r.seq > max)) max = r.seq;
  }
  return max;
}

/** Ungelesen = Assistent-Nachrichten nach last_seen (eigene Nachrichten zaehlen nicht). */
export function countUnread(rows, lastSeen) {
  if (lastSeen == null) return 0;
  return (rows || []).filter((r) => r.role === 'assistant' && r.seq > lastSeen).length;
}

/**
 * Server-Zeilen zu Anzeige-Nachrichten gruppieren: eine assistant-Zeile nimmt
 * die direkt folgenden 'tool'-Zeilen (tool_result) mit auf, damit die
 * Tool-Karten ihr Ergebnis finden. Ergebnis sind Roh-Nachrichten im Format des
 * bestehenden Konversations-GET ({role, content, parts}) plus:
 *   _seq  = erste seq der Gruppe (Sortier-/Dedupe-Schluessel)
 *   _id   = 'seq-<n>' (stabil ueber Reloads/Polls)
 *   _hash = Inhalts-Fingerprint (unveraenderte Gruppen behalten ihren UI-State)
 */
export function groupThreadRows(rows) {
  const sorted = [...(rows || [])].sort((a, b) => a.seq - b.seq);
  const out = [];
  for (const r of sorted) {
    // nexus_card-Parts merken sich die seq IHRER Zeile — card-action braucht
    // genau diese seq, auch wenn die Zeile in eine Gruppe eingefaltet wird.
    const parts = (Array.isArray(r.parts) ? r.parts : []).map((p) => (
      p && p.type === 'nexus_card' ? { ...p, _row_seq: r.seq } : p
    ));
    const last = out[out.length - 1];
    if (r.role === 'tool' && last && last.role === 'assistant') {
      last.parts = [...last.parts, ...parts];
      last._seqs.push(r.seq);
      last._lastSeq = r.seq;
      continue;
    }
    // Verwaiste tool-Zeile (Seitenanfang) → als Assistent-Gruppe fuehren.
    const role = r.role === 'user' ? 'user' : 'assistant';
    out.push({
      role,
      parts: [...parts],
      created_at: r.created_at || null,
      timestamp: r.created_at || null,
      _seq: r.seq,
      _seqs: [r.seq],
      _id: `seq-${r.seq}`,
    });
    out[out.length - 1]._lastSeq = r.seq;
  }
  for (const g of out) {
    g.content = g.parts
      .filter((p) => p && p.type === 'text' && p.text)
      .map((p) => p.text)
      .join('\n')
      .trim();
    try {
      g._hash = JSON.stringify(g.parts);
    } catch {
      g._hash = String(Math.random());
    }
  }
  return out;
}

/** Zeilen per seq zusammenfuehren (neuere Server-Version gewinnt), aufsteigend sortiert. */
export function mergeRows(existing, incoming) {
  const bySeq = new Map();
  for (const r of existing || []) bySeq.set(r.seq, r);
  for (const r of incoming || []) bySeq.set(r.seq, r);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export function minSeqOf(rows) {
  let min = null;
  for (const r of rows || []) {
    if (Number.isFinite(r?.seq) && (min == null || r.seq < min)) min = r.seq;
  }
  return min;
}
